import { type FC } from 'react';
import { toast } from 'react-toastify';
import MODALS from '@constants/modals';
import { MAX_SAVED_VIEWS } from '@domain/schemas/saved-view.schema';
import { useQueryFilters } from '@features/query-builder';
import { useModalDispatch } from '@hooks/useModalProvider';
import { useSavedViews } from '@queries/fetchSavedViews';
import { useIsMutating } from '@tanstack/react-query';

import { SAVED_VIEWS_ENTITY } from '../constants';
import { useActiveSavedView } from '../hooks/useActiveSavedView';
import { useSavedViewDirty } from '../hooks/useSavedViewDirty';
import { UPDATE_SAVED_VIEW_MUTATION_KEY, useUpdateSavedView } from '../hooks/useSavedViewMutations';
import type { SavedViewsActionsMode } from '../views/SavedViewsActions';
import { SavedViewsActions } from '../views/SavedViewsActions';

export const SavedViewsActionsController: FC = () => {
  const { showModal } = useModalDispatch();
  const { data: views = [] } = useSavedViews();
  const filters = useQueryFilters(SAVED_VIEWS_ENTITY);
  const activeView = useActiveSavedView();
  const isDirty = useSavedViewDirty(activeView?.filters, SAVED_VIEWS_ENTITY);
  const { mutate: update } = useUpdateSavedView();
  // While a rename/update PUT is in flight, the cached activeView is stale — composing another
  // full {name, filters} payload from it would silently revert the in-flight edit.
  const isUpdating = useIsMutating({ mutationKey: UPDATE_SAVED_VIEW_MUTATION_KEY }) > 0;

  const deriveMode = (): SavedViewsActionsMode | null => {
    if (activeView) {
      return {
        kind: 'activeView',
        canSaveChanges: isDirty && !isUpdating && filters.length > 0,
        onSaveChanges: () =>
          update(
            { id: activeView.id, name: activeView.name, filters },
            {
              onSuccess: () => toast.success('Filtered view updated'),
              onError: () => toast.error('Unable to update view'),
            },
          ),
        onRemoveView: () => showModal(MODALS.REMOVE_VIEW, { viewId: activeView.id }),
      };
    }
    if (filters.length === 0) return null;
    if (views.length >= MAX_SAVED_VIEWS) return { kind: 'limitReached', maxViews: MAX_SAVED_VIEWS };
    return { kind: 'savePrompt', onSaveView: () => showModal(MODALS.SAVE_VIEW, { filters }) };
  };

  const mode = deriveMode();
  return mode && <SavedViewsActions mode={mode} />;
};
